import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Input } from "../ui/input";
import { MessageFormData, messageSchema } from "../../schemas/message";
import { useEffect, useState } from "react";
import { cn } from "../../lib/utils";
import {
  Save,
  Send,
  MessageSquare,
  Type,
  Palette,
  Square,
  Upload,
} from "lucide-react";

interface MessageFormProps {
  onSubmit: (data: MessageFormData) => Promise<void>;
  onSend?: (data: MessageFormData) => Promise<void>;
  defaultValues?: Partial<MessageFormData>;
  children?: React.ReactElement<{ date?: Date }>;
  isScheduled?: boolean;
  isLoading?: boolean;
}

export const MessageForm = ({
  onSubmit,
  onSend,
  defaultValues,
  children,
  isLoading,
  isScheduled,
}: MessageFormProps) => {
  const { t } = useTranslation();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [mediaType, setMediaType] = useState<
    "image" | "video" | "audio" | null
  >(null);

  const form = useForm<MessageFormData>({
    resolver: zodResolver(messageSchema),
    defaultValues: defaultValues || {
      content: {
        type: "text",
        text: {
          body: "",
          font: "SYSTEM",
          text_color: "#000000",
          background_color: "#ffffff",
        },
      },
    },
  });

  const handleFileDrop = (files: FileList | null) => {
    if (!files?.length) return;

    const file = files[0];
    setSelectedFile(file);

    // Create preview URL
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);

    // Get current text content
    const currentText = (form.getValues("content.text") || {}).body || "";

    // Determine media type from mime type
    if (file.type.startsWith("image/")) {
      setMediaType("image");
      form.setValue("content", {
        type: "image",
        image: {
          filename: file.name,
          link: url,
          caption: currentText,
        },
      });
    } else if (file.type.startsWith("video/")) {
      setMediaType("video");
      form.setValue("content", {
        type: "video",
        video: {
          filename: file.name,
          link: url,
          caption: currentText,
        },
      });
    } else if (file.type.startsWith("audio/")) {
      setMediaType("audio");
      form.setValue("content", {
        type: "audio",
        audio: {
          filename: file.name,
          link: url,
          caption: currentText,
        },
      });
    }

    form.clearErrors("content.text.body");
  };

  // Reset form when file is removed
  useEffect(() => {
    if (!selectedFile) {
      const currentText = form.getValues("content.text");
      form.setValue("content", {
        type: "text",
        text: {
          body: currentText?.body || "",
          font: currentText?.font || "SYSTEM",
          text_color: currentText?.text_color || "#000000",
          background_color: currentText?.background_color || "#ffffff",
        },
      });
      setMediaType(null);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }
    }
  }, [selectedFile, form, previewUrl]);

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const renderPreview = () => {
    if (!previewUrl || !mediaType) return null;

    switch (mediaType) {
      case "image":
        return (
          <img
            src={previewUrl}
            alt="Preview"
            className="h-full w-full rounded-lg object-cover"
          />
        );
      case "video":
        return (
          <video
            src={previewUrl}
            className="h-full w-full rounded-lg object-cover"
            controls={false}
          />
        );
      case "audio":
        return (
          <div className="flex h-full items-center justify-center">
            <audio src={previewUrl} className="w-full max-w-[200px]" controls />
          </div>
        );
      default:
        return null;
    }
  };

  const uploadIcon = (
    <svg
      className={cn(
        "h-12 w-12",
        selectedFile ? "text-green-500" : "text-gray-400"
      )}
      stroke="currentColor"
      fill="none"
      viewBox="0 0 48 48"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
      />
    </svg>
  );

  const buttonText = isLoading
    ? isScheduled
      ? t("message.form.scheduling")
      : t("message.form.sending")
    : isScheduled
    ? t("schedule.schedule")
    : t("message.send");

  return (
    <Form {...form}>
      <form className="space-y-6">
        {!mediaType ? (
          <>
            <FormField
              control={form.control}
              name="content.text.body"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" />
                    {t("message.form.message_label")}
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t("message.form.message_placeholder")}
                      className="min-h-[120px] text-lg leading-relaxed p-4"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="content.text.font"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      <Type className="h-4 w-4" />
                      {t("message.form.font_label")}
                    </FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue translate="no" placeholder={t("message.form.select_font_placeholder")} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="SYSTEM">System</SelectItem>
                        <SelectItem value="SYSTEM_TEXT">System Text</SelectItem>
                        <SelectItem value="FB_SCRIPT">FB Script</SelectItem>
                        <SelectItem value="SYSTEM_BOLD">System Bold</SelectItem>
                        <SelectItem value="MORNINGBREEZE_REGULAR">Morning Breeze</SelectItem>
                        <SelectItem value="CALISTOGA_REGULAR">Calistoga</SelectItem>
                        <SelectItem value="EXO2_EXTRABOLD">Exo 2</SelectItem>
                        <SelectItem value="COURIERPRIME_BOLD">Courier Prime</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="content.text.text_color"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      <Palette className="h-4 w-4" />
                      {t("message.form.text_color_label")}
                    </FormLabel>
                    <FormControl>
                      <Input type="color" {...field} className="h-10 px-2 py-1" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="content.text.background_color"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      <Square className="h-4 w-4" />
                      {t("message.form.background_color_label")}
                    </FormLabel>
                    <FormControl>
                      <Input type="color" {...field} className="h-10 px-2 py-1" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </>
        ) : (
          <FormField
            control={form.control}
            name={`content.${mediaType}.caption`}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  {t("message.form.caption_label")}
                </FormLabel>
                <FormControl>
                  <Textarea
                    placeholder={t("message.form.caption_placeholder")}
                    className="min-h-[120px] text-lg leading-relaxed p-4"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name={mediaType ? `content.${mediaType}.filename` : "content.text.font"}
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2">
                <Upload className="h-4 w-4" />
                {t("message.form.media_file_label")}
              </FormLabel>
              <FormControl>
                <div
                  className={cn(
                    "relative grid min-h-[100px] sm:min-h-[150px] cursor-pointer grid-cols-1 sm:grid-cols-[1fr,200px] gap-4 rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-center transition-colors hover:bg-gray-100",
                    isDragging && "border-primary bg-primary/10",
                    selectedFile && "border-green-500 bg-green-50"
                  )}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setIsDragging(true);
                  }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setIsDragging(false);
                    handleFileDrop(e.dataTransfer.files);
                  }}
                  onClick={() => {
                    const input = document.createElement("input");
                    input.type = "file";
                    input.accept = "image/*,video/*,audio/*";
                    input.onchange = (e) => {
                      const target = e.target as HTMLInputElement;
                      handleFileDrop(target.files);
                    };
                    input.click();
                  }}
                >
                  <div className="flex flex-col items-center justify-center space-y-2">
                    {uploadIcon}
                    <div>
                      <p className="text-sm font-medium text-gray-600">
                        {selectedFile
                          ? selectedFile.name
                          : t("message.form.drag_drop_text")}
                      </p>
                      {!selectedFile && (
                        <p className="text-xs text-gray-500">
                          {t("message.form.supported_files")}
                        </p>
                      )}
                      {selectedFile && (
                        <button
                          type="button"
                          className="mt-2 text-sm text-primary hover:text-primary/80"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedFile(null);
                          }}
                        >
                          {t("message.form.remove_file")}
                        </button>
                      )}
                    </div>
                  </div>
                  {(selectedFile || !window.matchMedia("(max-width: 640px)").matches) && (
                    <div className="relative flex items-center justify-center overflow-hidden rounded-lg bg-black/5 h-48 w-full sm:w-48">
                      {renderPreview()}
                    </div>
                  )}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {children}

        <div className="flex flex-col sm:flex-row gap-4">
          {onSend && (
            <Button
              type="button"
              className="w-full sm:flex-1 flex-3 bg-primary hover:bg-primary/80"
              disabled={isLoading}
              onClick={form.handleSubmit(onSend)}
            >
              <Send className="mr-2 h-4 w-4" /> {buttonText}
            </Button>
          )}
          <Button
            type="button"
            className="w-full sm:flex-2 flex-1 bg-gray-500 hover:bg-gray-600"
            disabled={isLoading}
            onClick={form.handleSubmit(onSubmit)}
          >
            <Save className="mr-2 h-4 w-4" />{" "}
            {isLoading ? t("message.form.saving") : t("message.form.save_draft")}
          </Button>
        </div>
      </form>
    </Form>
  );
};
